import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, ArrowLeft, ArrowRight, ArrowUp } from 'lucide-react';
import QuickDrawCard from './QuickDrawCard';
import { extractPDFRegion, clearPdfCache } from './PDFUtils';

// Maximum number of snippets to keep in cache
const MAX_CACHE_SIZE = 10;

const QuickDrawEditor = ({
  tableData,
  onClose,
  onUpdate,
  isDarkMode,
  projectType,
  viewerRef,
  pdfUrl,
  imageUrl,
  includeBlankCells
}) => {
  const [cells, setCells] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalCells, setTotalCells] = useState(0);
  const [snippetCache, setSnippetCache] = useState({});
  const [isComplete, setIsComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [editedContent, setEditedContent] = useState('');
  const [currentCellLoading, setCurrentCellLoading] = useState(false);
  const [viewHistory, setViewHistory] = useState([]);
  const [canGoBack, setCanGoBack] = useState(false);
  
  // Track cells being processed to avoid duplicate requests
  const processingCellsRef = useRef(new Set());
  // Track which cells have been processed for prefetching
  const processedCellsRef = useRef(new Set());
  // Track processed cell IDs to prevent duplicates in progress
  const processedCellIdsRef = useRef(new Set());
  // Track if the component is initialized to prevent unnecessary recomputation
  const isInitializedRef = useRef(false);
  // Store snapshot of cells to prevent resetting on tableData changes
  const cellsSnapshotRef = useRef([]);
  // Store currentIndex to maintain state between renders
  const currentIndexRef = useRef(0);


  const isValidBoundingBox = (boundingBox) => {
    if (!boundingBox) return false;
    
    // Check if all required properties exist and are numbers
    const hasRequiredProps = 
      typeof boundingBox.Left === 'number' && 
      typeof boundingBox.Top === 'number' && 
      typeof boundingBox.Width === 'number' && 
      typeof boundingBox.Height === 'number';
    
    // Check if values are in valid range (0-1 for percentages)
    const hasValidValues = 
      boundingBox.Left >= 0 && boundingBox.Left <= 1 &&
      boundingBox.Top >= 0 && boundingBox.Top <= 1 &&
      boundingBox.Width > 0 && boundingBox.Width <= 1 &&
      boundingBox.Height > 0 && boundingBox.Height <= 1;
    
    return hasRequiredProps && hasValidValues;
  };
  
  // Utility function to create a fallback boundingBox if the original is invalid
  const createFallbackBoundingBox = (cell, tableData) => {
    // If we have table boundingBox, create a cell bounding box based on grid position
    if (tableData?.boundingBox) {
      const { boundingBox: tableBB } = tableData;
      const { rowCount, columnCount } = tableData.structure;
      
      // Calculate cell position within table grid
      const cellWidth = tableBB.Width / columnCount;
      const cellHeight = tableBB.Height / rowCount;
      
      return {
        Left: tableBB.Left + (cell.col * cellWidth),
        Top: tableBB.Top + (cell.row * cellHeight),
        Width: cellWidth,
        Height: cellHeight
      };
    }
    
    // If no table boundingBox, create a generic centered boundingBox
    return {
      Left: 0.4,
      Top: 0.4,
      Width: 0.2,
      Height: 0.2
    };
  };

  const handlePrevious = () => {
    if (viewHistory.length === 0) return;
    
    // Get the previous index from history
    const prevIndex = viewHistory[viewHistory.length - 1];
    
    // Update the history by removing the last item
    setViewHistory(prev => prev.slice(0, prev.length - 1));
    
    // Update can go back state
    setCanGoBack(viewHistory.length > 1);
    
    // Set the current index to the previous one
    setCurrentIndex(prevIndex);
    currentIndexRef.current = prevIndex;
    
    // Set edited content for the previous cell
    if (cells[prevIndex]) {
      setEditedContent(cells[prevIndex].content || '');
    }
  };

  useEffect(() => {
    // Reset initialization when structure changes or highlights change
    const hasStructureChanged = tableData && (
      !cellsSnapshotRef.current.length ||
      tableData.structure.rowCount !== tableData.currentData.length ||
      tableData.structure.columnCount !== tableData.currentData[0]?.length
    );
    
    // New: Check if highlights have changed - compare number of red-highlighted cells
    const currentRedHighlights = tableData?.structure?.highlights?.filter(h => h.color === '#f44336')?.length || 0;
    const previousRedHighlights = cellsSnapshotRef.current.length;
    const hasHighlightsChanged = currentRedHighlights !== previousRedHighlights;
    
    if (hasStructureChanged || hasHighlightsChanged) {
      console.log('Table structure or highlights have changed, resetting initialization');
      isInitializedRef.current = false;
      cellsSnapshotRef.current = [];
    }

    // Skip if already initialized and no relevant changes
    if (isInitializedRef.current && !hasStructureChanged && !hasHighlightsChanged) {
      console.log('QuickDraw already initialized, skipping cell extraction');
      return;
    }
    
    if (!tableData) {
      setIsLoading(false);
      return;
    }

    console.log('Initializing QuickDraw with tableData');
    const { currentData, textractMetadata, structure } = tableData;
    const highlights = structure?.highlights || [];
    
    // Collect red-highlighted cells only
    const redCells = [];
    
    // Reset processed cells tracking
    processedCellIdsRef.current.clear();
    
    // Gather red-highlighted cells
    highlights
      .filter(h => h.color === '#f44336') // Filter only red highlights
      .forEach(h => {
        // Skip cells outside current table dimensions
        if (h.row >= currentData.length || h.col >= currentData[0].length) {
          console.log(`Skipping highlight at [${h.row},${h.col}] - outside table dimensions`);
          return;
        }
        
        const cellId = `${h.row}-${h.col}`;

        // Skip blank cells if includeBlankCells is false
        if (!includeBlankCells && (!currentData[h.row][h.col] || currentData[h.row][h.col].trim() === '')) {
          console.log(`Skipping blank cell at [${h.row},${h.col}] - blank cells excluded`);
          return;
        }

        if (!processedCellIdsRef.current.has(cellId)) {
          redCells.push({ row: h.row, col: h.col });
          processedCellIdsRef.current.add(cellId);
        }
      });
    
    console.log(`Found ${redCells.length} red-highlighted cells for quick draw review`);
    
    // Process all gathered cells, keeping only those with valid bounding boxes
    const reviewCells = redCells
      .map(({ row, col }) => {
        // Double-check that cell is within current table dimensions 
        if (row >= currentData.length || col >= currentData[0].length) {
          console.log(`Skipping cell at [${row},${col}] - outside table dimensions`);
          return null;
        }
        
        // Get cell metadata if available
        const metadata = textractMetadata?.cells?.[row]?.[col];
        const boundingBox = metadata?.boundingBox;
        
        // Some older projects might not have proper bounding box data
        // We'll create a default if missing but still show the cell
        let validBoundingBox = boundingBox;
        
        if (!boundingBox || !boundingBox.Width || !boundingBox.Height) {
          if (tableData.boundingBox) {
            // Use table's bounding box as fallback
            validBoundingBox = {
              Left: tableData.boundingBox.Left || 0,
              Top: tableData.boundingBox.Top || 0,
              Width: (tableData.boundingBox.Width || 1) / tableData.structure.columnCount,
              Height: (tableData.boundingBox.Height || 1) / tableData.structure.rowCount
            };
          } else {
            // No usable bounding box data
            return null;
          }
        }
        
        return {
          row,
          col,
          content: currentData?.[row]?.[col] || '',
          boundingBox: validBoundingBox,
          confidence: metadata?.confidence || 0,
          pageNumber: tableData.pageNumber,
          // Store current table dimensions to detect structure changes
          tableRowCount: currentData.length,
          tableColCount: currentData[0].length
        };
      })
      .filter(Boolean); // Remove null entries
    
    console.log(`${reviewCells.length} cells have valid coordinates for quick draw review`);
    
    // Store cells and set initialized flag
    setCells(reviewCells);
    cellsSnapshotRef.current = reviewCells;
    setTotalCells(reviewCells.length);
    setCurrentIndex(0);
    currentIndexRef.current = 0;
    setIsComplete(reviewCells.length === 0);
    setIsLoading(reviewCells.length > 0);
    isInitializedRef.current = true;
    
    // Reset view history when cells change
    setViewHistory([]);
    setCanGoBack(false);
    
    // Reset cache and processed cells
    setSnippetCache({});
    processedCellsRef.current.clear();
    processingCellsRef.current.clear();
    
    // Set edited content for the first cell
    if (reviewCells.length > 0) {
      setEditedContent(reviewCells[0].content || '');
    }
    
    // Clear any active cell selection in the main table
    document.activeElement?.blur();
  }, [tableData?.id, tableData?.structure?.rowCount, tableData?.structure?.columnCount, 
      // Add dependency on highlights to trigger recalculation when they change
      tableData?.structure?.highlights, includeBlankCells]);

  useEffect(() => {
    // Skip if not initialized yet or no table data
    if (!isInitializedRef.current || !tableData || cells.length === 0) return;
  
    // Check if table dimensions have changed since initialization
    const currentRows = tableData.currentData.length;
    const currentCols = tableData.currentData[0]?.length || 0;
    const storedRows = cells[0]?.tableRowCount;
    const storedCols = cells[0]?.tableColCount;
  
    if (storedRows !== undefined && storedCols !== undefined && 
        (currentRows !== storedRows || currentCols !== storedCols)) {
      console.log('Table structure changed during QuickDraw session', {
        original: `${storedRows}x${storedCols}`,
        current: `${currentRows}x${currentCols}`
      });
  
      // Filter out cells that are now outside table boundaries
      const validCells = cells.filter(cell => 
        cell.row < currentRows && cell.col < currentCols
      );
  
      if (validCells.length !== cells.length) {
        console.log(`Filtered out ${cells.length - validCells.length} cells that are now outside table boundaries`);
        
        // Update cells state with valid cells only
        setCells(validCells);
        cellsSnapshotRef.current = validCells;
        setTotalCells(validCells.length);
        
        // If we've removed the current cell or all cells, adjust accordingly
        if (validCells.length === 0) {
          setIsComplete(true);
        } else if (currentIndex >= validCells.length) {
          setCurrentIndex(0);
          currentIndexRef.current = 0;
          setEditedContent(validCells[0]?.content || '');
        }
      }
    }
  }, [tableData?.currentData?.length, tableData?.currentData?.[0]?.length, cells, currentIndex]);

  // Update cell content when tableData changes (after approving a cell)
  useEffect(() => {
    // Only run this effect after initialization
    if (!isInitializedRef.current || !tableData || cells.length === 0) return;
    
    // Update content of cells from tableData to reflect latest edits
    const updatedCells = cellsSnapshotRef.current.map(cell => ({
      ...cell,
      content: tableData.currentData?.[cell.row]?.[cell.col] || cell.content || ''
    }));
    
    setCells(updatedCells);
    cellsSnapshotRef.current = updatedCells;
    
    // Make sure we update the edited content for the current cell
    if (currentIndexRef.current < updatedCells.length) {
      const currentCell = updatedCells[currentIndexRef.current];
      if (currentCell) {
        setEditedContent(currentCell.content || '');
      }
    }
  }, [tableData?.currentData]);

  // In QuickDrawEditor.js, update the useEffect for loading snippets

  useEffect(() => {
    if (!isInitializedRef.current) return;
    if (cells.length === 0 || isComplete) return;
    if (currentIndex < 0 || currentIndex >= cells.length) {
      console.warn(`Invalid currentIndex: ${currentIndex}, cells length: ${cells.length}`);
      return;
    }
    
    const currentCell = cells[currentIndex];
    if (!currentCell) {
      console.warn(`No cell found at index ${currentIndex}`);
      return;
    }
    
    // Update the ref to maintain state
    currentIndexRef.current = currentIndex;
    
    // Function to load a specific cell's snippet
    const loadCellSnippet = async (cell, index) => {
      // Skip if already processing or processed
      const cellKey = `${cell.row}-${cell.col}`;
      if (processingCellsRef.current.has(cellKey) || 
          snippetCache[cellKey]) {
        return;
      }
      
      // Mark as processing
      processingCellsRef.current.add(cellKey);
      
      try {
        // Set loading state for current cell only
        if (index === currentIndex) {
          setCurrentCellLoading(true);
        }
        
        let snippet = null;
        
        // For PDF projects, generate snippet as before
        if (projectType === 'pdf' && pdfUrl) {
          snippet = await extractPDFRegion(
            pdfUrl,
            cell.pageNumber,
            cell.boundingBox,
            0.3 // padding
          );
          
          // Only update the cache for PDF projects
          if (snippet) {
            setSnippetCache(prevCache => ({
              ...prevCache,
              [cellKey]: snippet
            }));
          }
        }
        // For image projects, we'll use CSS positioning instead of canvas
        // Just mark it as processed, but don't generate a snippet
      } catch (error) {
        console.error(`Error loading snippet for cell [${cell.row},${cell.col}]:`, error);
      } finally {
        // Mark as no longer processing
        processingCellsRef.current.delete(cellKey);
        if (index === currentIndex) {
          setCurrentCellLoading(false);
        }
        // Mark as processed for prefetch tracking
        processedCellsRef.current.add(cellKey);
      }
    };
    
    // Load current cell's snippet
    loadCellSnippet(currentCell, currentIndex);
    
    // Prefetch the next cell's snippet (only for PDFs)
    if (projectType === 'pdf' && currentIndex + 1 < cells.length) {
      const nextCell = cells[currentIndex + 1];
      const nextCellKey = `${nextCell.row}-${nextCell.col}`;
      
      if (!snippetCache[nextCellKey] && !processedCellsRef.current.has(nextCellKey)) {
        // Queue the next cell with a small delay to prioritize current cell
        setTimeout(() => {
          loadCellSnippet(nextCell, currentIndex + 1);
        }, 200);
      }
    }
    
    // Initialize edited content for current cell
    setEditedContent(currentCell.content || '');
  }, [currentIndex, cells, isComplete, pdfUrl, imageUrl, projectType, snippetCache]);

  // Isolate keyboard events from the main application
  // Update the keyboard event listener to include handling the 'up' arrow key
  useEffect(() => {
    // Setup global keyboard controls
    const handleKeyDown = (e) => {
      // Only handle these specific keys to prevent propagation issues
      if (['Escape', 'ArrowRight', 'ArrowLeft', 'ArrowUp'].includes(e.key)) {
        // Stop propagation to prevent affecting the main table
        e.stopPropagation();
        e.preventDefault();
        
        if (e.key === 'Escape') {
          onClose();
        } else if (e.key === 'ArrowRight' && !isComplete) {
          handleApprove(editedContent);
        } else if (e.key === 'ArrowLeft' && !isComplete) {
          handleSkip();
        } else if (e.key === 'ArrowUp' && canGoBack && !isComplete) {
          handlePrevious();
        }
      }
    };

    // Add the event listener
    window.addEventListener('keydown', handleKeyDown, true); // Capture phase
    
    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isComplete, editedContent, canGoBack]);

  const handleContentChange = (content) => {
    setEditedContent(content);
  };

  const handleApprove = (content) => {
    const currentCell = cells[currentIndex];
    if (!currentCell) return;
  
    // Add current index to history before moving forward
    setViewHistory(prev => [...prev, currentIndex]);
    setCanGoBack(true);
  
    // Update the cell in the parent component
    onUpdate(
      currentCell.row, 
      currentCell.col, 
      content, 
      '#4caf50'  // Green highlight
    );
  
    // Move to next cell if available, otherwise complete
    if (currentIndex + 1 < cells.length) {
      // Use state update to avoid race conditions
      setCurrentIndex(prevIndex => prevIndex + 1);
    } else {
      setIsComplete(true);
    }
  };
  
  const handleSkip = () => {
    const currentCell = cells[currentIndex];
    if (!currentCell) return;
    
    // Add current index to history before moving forward
    setViewHistory(prev => [...prev, currentIndex]);
    setCanGoBack(true);
    
    // Update the cell to ensure it has a red highlight
    onUpdate(
      currentCell.row, 
      currentCell.col, 
      currentCell.content, // Keep the original content
      '#f44336'  // Red highlight
    );
    
    // Move to next cell without changing anything else
    if (currentIndex + 1 < cells.length) {
      // Use state update to avoid race conditions
      setCurrentIndex(prevIndex => prevIndex + 1);
    } else {
      setIsComplete(true);
    }
  };
  

  // Render the completion screen
  const renderCompletionScreen = () => (
    <div className="text-center py-10">
      <div className={`text-lg font-medium mb-6 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
        All Done! 🎉
      </div>
      
      <div className={`mb-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        You've reviewed all {totalCells} cells.
      </div>
      
      <button
        onClick={onClose}
        className={`px-4 py-2 rounded-lg ${
          isDarkMode 
            ? 'bg-blue-600 hover:bg-blue-500 text-white' 
            : 'bg-blue-500 hover:bg-blue-600 text-white'
        }`}
      >
        Return to Workspace
      </button>
    </div>
  );

  // Render loading state
  if (isLoading && cells.length === 0) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50">
        <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'} opacity-80`}></div>
        <div className={`z-10 p-6 rounded-xl shadow-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 rounded-full border-4 border-blue-500 border-t-transparent animate-spin mb-4"></div>
            <p className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>
              Finding cells to review...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Create a wrapper to capture and stop propagation of all keyboard/mouse events
  const stopPropagation = (e) => {
    e.stopPropagation();
  };

  return (
    // Capture mouse/touch events at this level to prevent affecting main table
    <div 
      className="fixed inset-0 flex items-center justify-center z-50"
      onKeyDown={stopPropagation}
      onClick={stopPropagation}
      onMouseDown={stopPropagation}
    >
      {/* Backdrop */}
      <div 
        className={`absolute inset-0 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'} opacity-80`}
        onClick={onClose}
      ></div>
      
      {/* Content */}
      <div 
        className={`z-10 p-6 rounded-xl shadow-xl w-full max-w-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h3 className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
            {isComplete
              ? 'Review Complete'
              : `Quick Draw Editor (${currentIndex + 1}/${totalCells})`
            }
          </h3>
          <button
            onClick={onClose}
            className={`p-1.5 rounded-full ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
          >
            <X size={18} className={isDarkMode ? 'text-gray-400' : 'text-gray-600'} />
          </button>
        </div>
        
        {/* Main content */}
        <div className="relative min-h-[300px] flex items-center justify-center">
          {isComplete ? (
            renderCompletionScreen()
          ) : (
            cells.length > 0 && currentIndex < cells.length && (
              <>
                {currentCellLoading ? (
                  <div className="flex flex-col items-center justify-center h-[300px]">
                    <div className="w-10 h-10 rounded-full border-3 border-blue-500 border-t-transparent animate-spin mb-3"></div>
                    <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                      Loading cell preview...
                    </p>
                  </div>
                ) : (
                  <QuickDrawCard
                    cellData={cells[currentIndex]}
                    snippet={snippetCache[`${cells[currentIndex].row}-${cells[currentIndex].col}`]}
                    cellContent={editedContent}
                    onContentChange={handleContentChange}
                    onApprove={handleApprove}
                    onSkip={handleSkip}
                    onPrevious={handlePrevious} // Add this prop
                    canGoBack={canGoBack} // Add this prop
                    isDarkMode={isDarkMode}
                    imageUrl={imageUrl}
                  />
                )}
              </>
            )
          )}
        </div>
        
        {/* Progress indicator */}
        {!isComplete && totalCells > 0 && (
          <div className="mt-6">
            <div className="h-1 w-full bg-gray-300 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-500"
                style={{ width: `${((currentIndex) / totalCells) * 100}%` }}
              ></div>
            </div>
            <div className="flex justify-between mt-2 text-xs">
              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                {currentIndex} reviewed
              </span>
              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                {totalCells - currentIndex} remaining
              </span>
            </div>
          </div>
        )}
        
        {/* Keyboard shortcuts help */}
        <div className="mt-6 flex justify-center gap-4 text-xs">
          <div className={`flex items-center gap-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <ArrowUp size={14} />
            <span>Previous</span>
          </div>
          <div className={`flex items-center gap-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <ArrowLeft size={14} />
            <span>Skip</span>
          </div>
          <div className={`flex items-center gap-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <ArrowRight size={14} />
            <span>Approve</span>
          </div>
          <div className={`flex items-center gap-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <span>Esc</span>
            <span>Exit</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickDrawEditor;